const CHUNK_PATH_MARKER = '/_next/static/chunks/';

/** A poisoned chunk can be several MB; give the refetch room before abandoning it. */
const REFETCH_TIMEOUT_MS = 10_000;

/**
 * Chunk URLs this document loaded. Two sources because each misses cases:
 * Resource Timing drops entries once its buffer fills, and a dynamically
 * imported chunk's script tag can be gone by the time we look.
 */
function loadedChunkUrls(): readonly string[] {
  const urls = new Set<string>();

  try {
    for (const { name } of globalThis.performance.getEntriesByType(
      'resource',
    )) {
      if (name.includes(CHUNK_PATH_MARKER)) {
        urls.add(name);
      }
    }
  } catch {
    // No Resource Timing — the script tags below still cover the static chunks.
  }

  try {
    const scripts =
      globalThis.document.querySelectorAll<HTMLScriptElement>('script[src]');
    for (const { src } of Array.from(scripts)) {
      if (src.includes(CHUNK_PATH_MARKER)) {
        urls.add(src);
      }
    }
  } catch {
    // No DOM (server render, tests) — nothing to add.
  }

  return Array.from(urls);
}

/**
 * Re-downloads every loaded chunk with the HTTP cache bypassed.
 *
 * A chunk truncated in transit is cached as if complete, and `immutable` stops a
 * reload from revalidating it, so the same bad bytes fail to parse on every
 * attempt. `cache: 'reload'` is the only fetch mode that both skips the cache on
 * the way out and overwrites the stored entry on the way back; `no-cors` matches
 * how the script tag requested it, which is the entry that has to be replaced.
 *
 * Every chunk goes, not just the broken one: the parse error names the bad chunk
 * but the module-factory cascade it triggers does not, and by then the chunk that
 * failed never ran, so nothing on the stack points at it.
 */
export async function purgeChunkCache(): Promise<void> {
  await Promise.allSettled(
    loadedChunkUrls().map((url) =>
      fetch(url, {
        cache: 'reload',
        mode: 'no-cors',
        signal: AbortSignal.timeout(REFETCH_TIMEOUT_MS),
      }),
    ),
  );
}
